import { useState, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFinanceStore, useSettingsStore } from '@/store'
import { formatDateLocalized, getJalaliYearMonth, englishToFarsiNumber } from '@/lib/calendar'
import { JalaliDatePicker } from '@/components/ui/JalaliDatePicker'
import { exportFinancePNG, exportFinanceExcel } from '@/lib/reportExport'
import dayjs, { Dayjs } from 'dayjs'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts'

type FinanceType = 'income' | 'expense'
type ViewMode = 'dashboard' | 'analysis' | 'transactions'

interface MonthData {
  year: number
  month: number
  jalaliYear: number
  jalaliMonth: number
}

const COLORS = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
  income: '#059669',
  expense: '#dc2626',
}

export default function FinanceTracker() {
  const { t, i18n } = useTranslation()
  const { items, addItem, deleteItem } = useFinanceStore()
  const { calendar, language, incomeCategories, expenseCategories, addIncomeCategory, addExpenseCategory } = useSettingsStore()
  
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard')
  const [selectedMonth, setSelectedMonth] = useState<MonthData>(() => {
    const now = dayjs()
    if (calendar === 'jalali') {
      const jalaliDate = getJalaliYearMonth(now.toDate())
      return {
        year: now.year(),
        month: now.month() + 1,
        jalaliYear: jalaliDate.year,
        jalaliMonth: jalaliDate.month,
      }
    }
    return {
      year: now.year(),
      month: now.month() + 1,
      jalaliYear: 0,
      jalaliMonth: 0,
    }
  })

  const [newItemTitle, setNewItemTitle] = useState('')
  const [newItemAmount, setNewItemAmount] = useState('')
  const [newItemType, setNewItemType] = useState<FinanceType>('expense')
  const [newItemCategory, setNewItemCategory] = useState('')
  const [newItemDate, setNewItemDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const [newCategoryName, setNewCategoryName] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')

  const currentCategories = newItemType === 'income' ? incomeCategories : expenseCategories

  const handleAddItem = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (newItemTitle.trim() && newItemAmount && newItemCategory) {
      addItem({
        title: newItemTitle,
        amount: parseFloat(newItemAmount),
        type: newItemType,
        category: newItemCategory,
        date: dayjs(newItemDate).toISOString(),
      })
      setNewItemTitle('')
      setNewItemAmount('')
      setNewItemType('expense')
      setNewItemCategory('')
      setNewItemDate(dayjs().format('YYYY-MM-DD'))
    }
  }

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      if (newItemType === 'income') {
        addIncomeCategory(newCategoryName)
      } else {
        addExpenseCategory(newCategoryName)
      }
      setNewItemCategory(newCategoryName)
      setNewCategoryName('')
    }
  }

  // Get month label
  const getMonthLabel = (): string => {
    if (calendar === 'jalali') {
      const jalaliMonths = t('calendar.jalaliMonths', [
        'Farvardin', 'Ordibehesht', 'Khordad', 'Tir', 'Mordad', 'Shahrivar',
        'Mehr', 'Aban', 'Azar', 'Dey', 'Bahman', 'Esfand'
      ])
      return `${jalaliMonths[selectedMonth.jalaliMonth - 1]} ${selectedMonth.jalaliYear}`
    }
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    return `${months[selectedMonth.month - 1]} ${selectedMonth.year}`
  }

  // Filter items by selected month
  const monthItems = useMemo(() => {
    if (!Array.isArray(items)) return []
    return items.filter(item => {
      const itemDate = dayjs(item.date)
      if (calendar === 'jalali') {
        const itemJalali = getJalaliYearMonth(itemDate.toDate())
        return itemJalali.year === selectedMonth.jalaliYear && itemJalali.month === selectedMonth.jalaliMonth
      }
      return itemDate.year() === selectedMonth.year && itemDate.month() + 1 === selectedMonth.month
    })
  }, [items, selectedMonth, calendar])

  // Calculate statistics
  const stats = useMemo(() => {
    if (!Array.isArray(monthItems)) {
      return { income: 0, expense: 0, balance: 0, incomeByCategory: {}, expenseByCategory: {} }
    }
    const income = monthItems.filter(i => i.type === 'income').reduce((sum, i) => sum + i.amount, 0)
    const expense = monthItems.filter(i => i.type === 'expense').reduce((sum, i) => sum + i.amount, 0)
    const balance = income - expense
    
    // Category breakdown
    const incomeByCategory: Record<string, number> = {}
    const expenseByCategory: Record<string, number> = {}
    
    monthItems.forEach(item => {
      if (item.type === 'income') {
        incomeByCategory[item.category] = (incomeByCategory[item.category] || 0) + item.amount
      } else {
        expenseByCategory[item.category] = (expenseByCategory[item.category] || 0) + item.amount
      }
    })

    return { income, expense, balance, incomeByCategory, expenseByCategory }
  }, [monthItems])

  // Prepare chart data
  const incomeCategoryData = useMemo(() => 
    Object.entries(stats.incomeByCategory).map(([name, value]) => ({ name, value: Math.round(value) })),
    [stats.incomeByCategory]
  )

  const expenseCategoryData = useMemo(() => 
    Object.entries(stats.expenseByCategory).map(([name, value]) => ({ name, value: Math.round(value) })),
    [stats.expenseByCategory]
  )

  // Monthly trend data (last 12 months)
  const monthlyTrendData = useMemo(() => {
    if (!Array.isArray(items)) return []
    const data: { month: string; income: number; expense: number }[] = []
    for (let i = 11; i >= 0; i--) {
      let checkDate = dayjs().subtract(i, 'month')
      let monthLabel = ''
      let checkYear = checkDate.year()
      let checkMonthNum = checkDate.month() + 1

      if (calendar === 'jalali') {
        const jalali = getJalaliYearMonth(checkDate.toDate())
        checkYear = jalali.year
        checkMonthNum = jalali.month
        const months = ['فر', 'ار', 'خر', 'تیر', 'مر', 'شهر', 'مهر', 'آبا', 'آذر', 'دی', 'بهم', 'اسف']
        monthLabel = months[checkMonthNum - 1]
      } else {
        monthLabel = checkDate.format('MMM')
      }

      const monthIncome = items
        .filter(item => {
          const itemDate = dayjs(item.date)
          if (calendar === 'jalali') {
            const itemJalali = getJalaliYearMonth(itemDate.toDate())
            return itemJalali.year === checkYear && itemJalali.month === checkMonthNum && item.type === 'income'
          }
          return itemDate.year() === checkYear && itemDate.month() + 1 === checkMonthNum && item.type === 'income'
        })
        .reduce((sum, i) => sum + i.amount, 0)

      const monthExpense = items
        .filter(item => {
          const itemDate = dayjs(item.date)
          if (calendar === 'jalali') {
            const itemJalali = getJalaliYearMonth(itemDate.toDate())
            return itemJalali.year === checkYear && itemJalali.month === checkMonthNum && item.type === 'expense'
          }
          return itemDate.year() === checkYear && itemDate.month() + 1 === checkMonthNum && item.type === 'expense'
        })
        .reduce((sum, i) => sum + i.amount, 0)

      data.push({ month: monthLabel, income: Math.round(monthIncome), expense: Math.round(monthExpense) })
    }
    return data
  }, [items, calendar])

  // Previous month comparison
  const previousMonthStats = useMemo(() => {
    let prevYear = selectedMonth.year
    let prevMonth = selectedMonth.month - 1
    let prevJalaliYear = selectedMonth.jalaliYear
    let prevJalaliMonth = selectedMonth.jalaliMonth - 1

    if (calendar === 'jalali' && prevJalaliMonth === 0) {
      prevJalaliMonth = 12
      prevJalaliYear--
    } else if (calendar !== 'jalali' && prevMonth === 0) {
      prevMonth = 12
      prevYear--
    }

    const prevItems = Array.isArray(items) ? items.filter(item => {
      const itemDate = dayjs(item.date)
      if (calendar === 'jalali') {
        const itemJalali = getJalaliYearMonth(itemDate.toDate())
        return itemJalali.year === prevJalaliYear && itemJalali.month === prevJalaliMonth
      }
      return itemDate.year() === prevYear && itemDate.month() + 1 === prevMonth
    }) : []

    const prevIncome = prevItems.filter(i => i.type === 'income').reduce((sum, i) => sum + i.amount, 0)
    const prevExpense = prevItems.filter(i => i.type === 'expense').reduce((sum, i) => sum + i.amount, 0)

    return {
      income: prevIncome,
      expense: prevExpense,
      incomeChange: ((stats.income - prevIncome) / (prevIncome || 1)) * 100,
      expenseChange: ((stats.expense - prevExpense) / (prevExpense || 1)) * 100,
    }
  }, [selectedMonth, items, stats, calendar])

  const filteredTransactions = useMemo(() => {
    return monthItems.filter(item => {
      if (filterCategory !== 'all' && item.category !== filterCategory) return false
      return true
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [monthItems, filterCategory])

  return (
    <div className="h-full flex flex-col fade-in bg-gray-100 dark:bg-slate-900">
      <div className="flex-shrink-0 px-6 py-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800">
        <h1 className="text-4xl font-bold text-gray-800 dark:text-white">
          💰 {t('sidebar.finance')}
        </h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom px-6 py-6 bg-gray-100 dark:bg-slate-900 flex flex-col">

      {/* Month Selector */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 rounded-lg neumorphic p-4 mb-6 flex items-center justify-between">
        <button
          onClick={() => {
            setSelectedMonth(prev => {
              if (calendar === 'jalali') {
                let newMonth = prev.jalaliMonth - 1
                let newYear = prev.jalaliYear
                if (newMonth === 0) {
                  newMonth = 12
                  newYear--
                }
                return { ...prev, jalaliMonth: newMonth, jalaliYear: newYear }
              }
              let newMonth = prev.month - 1
              let newYear = prev.year
              if (newMonth === 0) {
                newMonth = 12
                newYear--
              }
              return { ...prev, month: newMonth, year: newYear }
            })
          }}
          className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:opacity-80 transition-opacity"
        >
          ← ماه قبل
        </button>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white text-center flex-1">
          {getMonthLabel()}
        </h2>
        <button
          onClick={() => {
            setSelectedMonth(prev => {
              if (calendar === 'jalali') {
                let newMonth = prev.jalaliMonth + 1
                let newYear = prev.jalaliYear
                if (newMonth === 13) {
                  newMonth = 1
                  newYear++
                }
                return { ...prev, jalaliMonth: newMonth, jalaliYear: newYear }
              }
              let newMonth = prev.month + 1
              let newYear = prev.year
              if (newMonth === 13) {
                newMonth = 1
                newYear++
              }
              return { ...prev, month: newMonth, year: newYear }
            })
          }}
          className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:opacity-80 transition-opacity"
        >
          ماه بعد →
        </button>
      </div>

      {/* Stats Display */}
      <div className="flex-shrink-0 grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg neumorphic p-4 border-r-4 border-green-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('finance.totalIncome')}</p>
          <p className="text-3xl font-bold text-green-600 dark:text-green-400">
            {Math.round(stats.income).toLocaleString('fa-IR')}
          </p>
          <p className={`text-xs mt-2 ${previousMonthStats.incomeChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {previousMonthStats.incomeChange >= 0 ? '+' : ''}{previousMonthStats.incomeChange.toFixed(0)}% نسبت به ماه قبل
          </p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg neumorphic p-4 border-r-4 border-red-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('finance.totalExpense')}</p>
          <p className="text-3xl font-bold text-red-600 dark:text-red-400">
            {Math.round(stats.expense).toLocaleString('fa-IR')}
          </p>
          <p className={`text-xs mt-2 ${previousMonthStats.expenseChange <= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {previousMonthStats.expenseChange >= 0 ? '+' : ''}{previousMonthStats.expenseChange.toFixed(0)}% نسبت به ماه قبل
          </p>
        </div>

        <div className={`rounded-lg neumorphic p-4 border-r-4 ${
          stats.balance >= 0
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500'
            : 'bg-orange-50 dark:bg-orange-900/20 border-orange-500'
        }`}>
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('finance.netBalance')}</p>
          <p className={`text-3xl font-bold ${
            stats.balance >= 0
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-orange-600 dark:text-orange-400'
          }`}>
            {Math.round(stats.balance).toLocaleString('fa-IR')}
          </p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg neumorphic p-4 border-r-4 border-purple-500">
          <p className="text-sm text-gray-600 dark:text-gray-400">تعداد تراکنش</p>
          <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{i18n.language === 'fa' ? englishToFarsiNumber(monthItems.length) : monthItems.length}</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
            {i18n.language === 'fa' ? englishToFarsiNumber(monthItems.filter(i => i.type === 'income').length) : monthItems.filter(i => i.type === 'income').length} درآمد، {i18n.language === 'fa' ? englishToFarsiNumber(monthItems.filter(i => i.type === 'expense').length) : monthItems.filter(i => i.type === 'expense').length} هزینه
          </p>
        </div>
      </div>

      {/* Export Reports Section */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 rounded-lg neumorphic p-4 mb-6 flex gap-2 items-center" id="finance-report-area">
        <button
          onClick={() => {
            const reportElement = document.querySelector('[data-finance-report]') as HTMLElement
            if (reportElement) {
              exportFinancePNG(reportElement)
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-semibold"
        >
          <span>🖼️</span>
          <span>خروجی تصویر</span>
        </button>
        
        <button
          onClick={() => {
            const reportData = {
              month: getMonthLabel(),
              year: selectedMonth.year,
              jalaliMonth: selectedMonth.jalaliMonth,
              jalaliYear: selectedMonth.jalaliYear,
              income: stats.income,
              expense: stats.expense,
              balance: stats.balance,
              incomeByCategoryData: Object.entries(stats.incomeByCategory).map(([name, value]) => ({ name, value: Math.round(value as number) })),
              expenseByCategoryData: Object.entries(stats.expenseByCategory).map(([name, value]) => ({ name, value: Math.round(value as number) })),
              items: filteredTransactions,
              calendar,
            }
            exportFinanceExcel(reportData)
          }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold"
        >
          <span>📊</span>
          <span>خروجی Excel</span>
        </button>
      </div>

      {/* View Mode Selector */}
      <div className="flex-shrink-0 flex gap-2 mb-6">
        <button
          onClick={() => setViewMode('dashboard')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'dashboard'
              ? 'bg-finance text-white'
              : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700'
          }`}
        >
          داشبورد
        </button>
        <button
          onClick={() => setViewMode('analysis')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'analysis'
              ? 'bg-finance text-white'
              : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700'
          }`}
        >
          تحلیل و نمودار
        </button>
        <button
          onClick={() => setViewMode('transactions')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'transactions'
              ? 'bg-finance text-white'
              : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-700'
          }`}
        >
          تراکنش‌ها
        </button>
      </div>

      {/* Content Section */}
      <div className="flex-1 space-y-6" data-finance-report>

      {viewMode === 'dashboard' && (
        <>
          {/* Add Transaction Form */}
          <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
            <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">ثبت تراکنش جدید</h2>
            <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
              <input
                type="text"
                placeholder="عنوان"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white md:col-span-2"
              />
              <input
                type="number"
                placeholder="مبلغ"
                value={newItemAmount}
                onChange={(e) => setNewItemAmount(e.target.value)}
                step="1000"
                className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white"
              />
              <select
                value={newItemType}
                onChange={(e) => {
                  setNewItemType(e.target.value as FinanceType)
                  setNewItemCategory('')
                }}
                className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white"
              >
                <option value="income">درآمد</option>
                <option value="expense">هزینه</option>
              </select>
              <select
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white"
              >
                <option value="">دسته‌بندی</option>
                {currentCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
                <option value="__new__">+ جدید</option>
              </select>
              
              {calendar === 'jalali' ? (
                <JalaliDatePicker
                  value={newItemDate}
                  onChange={(date) => setNewItemDate(date)}
                />
              ) : (
                <input
                  type="date"
                  value={newItemDate}
                  onChange={(e) => setNewItemDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white"
                />
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  handleAddItem()
                }}
                className="bg-finance text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold"
              >
                افزودن
              </button>
            </div>

            {newItemCategory === '__new__' && (
              <div className="mt-4 flex gap-2">
                <input
                  type="text"
                  placeholder="نام دسته‌بندی جدید"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-finance dark:text-white"
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity font-semibold"
                >
                  ایجاد
                </button>
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">آخرین تراکنش‌ها</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {filteredTransactions.slice(0, 10).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400">هیچ تراکنشی در این ماه ثبت نشده</p>
              ) : (
                filteredTransactions.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center justify-between p-3 rounded-lg border-r-4 ${
                      item.type === 'income'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 dark:text-white">{item.title}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.category} • {formatDateLocalized(item.date, calendar, language)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <p className={`font-bold text-lg ${
                        item.type === 'income'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {item.type === 'income' ? '+' : '-'}{Math.round(item.amount).toLocaleString('fa-IR')}
                      </p>
                      <button
                        onClick={() => deleteItem(item.id)}
                        className="text-red-500 hover:text-red-700 text-lg transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}

      {viewMode === 'analysis' && (
        <>
          {/* Category Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Income by Category */}
            <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">درآمد بر اساس دسته</h3>
              {incomeCategoryData.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">هیچ درآمدی ثبت نشده</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={incomeCategoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name} ${value}`}
                      outerRadius={100}
                      fill="#10b981"
                      dataKey="value"
                    >
                      {incomeCategoryData.map((item, index) => (
                        <Cell key={`income-cell-${item.name}-${index}`} fill={['#10b981', '#059669', '#047857', '#065f46'][index % 4]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString('fa-IR')} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Expense by Category */}
            <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
              <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">هزینه بر اساس دسته</h3>
              {expenseCategoryData.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">هیچ هزینه‌ای ثبت نشده</p>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={expenseCategoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name} ${value}`}
                      outerRadius={100}
                      fill="#ef4444"
                      dataKey="value"
                    >
                      {expenseCategoryData.map((item, index) => (
                        <Cell key={`expense-cell-${item.name}-${index}`} fill={['#ef4444', '#dc2626', '#b91c1c', '#991b1b'][index % 4]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => value.toLocaleString('fa-IR')} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
            <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">روند درآمد و هزینه (12 ماه گذشته)</h3>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={monthlyTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: number) => value.toLocaleString('fa-IR')} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name="درآمد" />
                <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2} name="هزینه" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {viewMode === 'transactions' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg neumorphic p-6">
          <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">لیست تراکنش‌ها</h3>
          
          <div className="mb-4">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 rounded-lg focus:outline-none dark:text-white"
            >
              <option value="all">تمام دسته‌ها</option>
              {[...incomeCategories, ...expenseCategories].map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            {filteredTransactions.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">هیچ تراکنشی موجود نیست</p>
            ) : (
              filteredTransactions.map((item) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-r-4 ${
                    item.type === 'income'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-500'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-500'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-white">{item.title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {item.category} • {formatDateLocalized(item.date, calendar, language)} • {dayjs(item.date).format('HH:mm')}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <p className={`font-bold text-lg whitespace-nowrap ${
                      item.type === 'income'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {item.type === 'income' ? '+' : '-'}{Math.round(item.amount).toLocaleString('fa-IR')}
                    </p>
                    <button
                      onClick={() => deleteItem(item.id)}
                      className="text-red-500 hover:text-red-700 text-lg transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      </div>
      </div>
    </div>
  )
}
